import React, { useCallback, useEffect, useState } from 'react';
import { AppBar, Tab, Tabs } from '@mui/material';
import { PDFManagerFactory } from 'pdftron';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate, useLocation, useParams } from 'react-router-dom';
import {
  app,
  getDifferencesLoadedSelector,
  getDocumentsLoaded,
  getInspectionCompleted,
  getInspectionHasRun,
  getInspectionId,
  getInspectionIsOutdated,
  getInspectionHasOutdatedGraphicZones,
  getInspectionSettings,
  getIsSingleFile,
  getLiveTextStatus,
  getSnackMessage,
  getLastJobCreatedAt,
  getBarcodeProfile,
  getGraphicsSensitivity,
  inspection,
} from 'store';
import { GraphicsSensitivity } from '@verify/shared-utils';
import { useCustomProfileById } from 'store/queries/organizations/useCustomProfiles';
import { LeftPanelTabIndexes, PDFTRON_DEFAULT_TOOL } from 'types';
import { isReportsUrl, isResultUrl } from 'utils/location';
import { fetchInspection } from 'store/request/inspections/actions';
import ResultTab from '../ResultPage/ResultTab';
import { useTracker } from '../../Tracker/TrackerProvider';
import PrepTab from './PrepTab';
import TabLabel from './TabLabel';
import * as Sentry from '@sentry/react';
import { useDocumentToastStateStore } from 'zstore/documentToastStateStore';
import { useInspectionCustomStatuses } from 'store/queries/organizations/useCustomStatuses';
import { useCollaborators } from 'store/queries/collaborators/useCollaborators';
import { isPrepLocked } from './sharedRedirect';
import GVTooltip from 'components/lib/GVToolTip/GVTooltip';
import { FlashAnimationManager } from 'pdftron/docManager/AnnotationTools/utils/FlashAnimationManager';
import styles from './InspectionLeftPanel.module.scss';

const segmentTabNames = {
  0: 'Prep-page',
  1: 'Results-page',
};
interface TabPanelProps {
  children: React.ReactElement;
  value: number;
  index: number;
}

const TabPanel = (props: TabPanelProps) => {
  const { children, value, index } = props;

  return (
    <div className={styles.tabContent} role="tabpanel" hidden={value !== index}>
      {children}
    </div>
  );
};

const InspectionLeftPanel = () => {
  const expireAllToast = useDocumentToastStateStore((s) => s.expireAllToast);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();
  const tracker = useTracker();
  const location = useLocation();

  const inspectionId = useSelector(getInspectionId);
  const inspectionHasRun = useSelector(getInspectionHasRun);
  const inspectionCompleted = useSelector(getInspectionCompleted);
  const inspectionIsOutdated = useSelector(getInspectionIsOutdated);
  const outdatedGraphicsTool = useSelector(getInspectionHasOutdatedGraphicZones);
  const lastJobCreatedAt = useSelector(getLastJobCreatedAt);
  const barcodeProfileId = useSelector(getBarcodeProfile);
  const graphicsSensitivity = useSelector(getGraphicsSensitivity);

  const graphicProfileId =
    graphicsSensitivity &&
    graphicsSensitivity !== GraphicsSensitivity.HIGH &&
    graphicsSensitivity !== GraphicsSensitivity.STANDARD
      ? graphicsSensitivity
      : undefined;

  const customBarcodeProfileId = barcodeProfileId && barcodeProfileId !== 'Default' ? barcodeProfileId : undefined;

  const { data: barcodeProfile } = useCustomProfileById(customBarcodeProfileId);
  const { data: graphicProfile } = useCustomProfileById(graphicProfileId);

  const isProfileStale =
    inspectionHasRun &&
    !!lastJobCreatedAt &&
    ((!!barcodeProfile?.updatedAt && new Date(barcodeProfile.updatedAt) > new Date(lastJobCreatedAt)) ||
      (!!graphicProfile?.updatedAt && new Date(graphicProfile.updatedAt) > new Date(lastJobCreatedAt)));
  const documentsLoaded = useSelector(getDocumentsLoaded);
  const differenceLoaded = useSelector(getDifferencesLoadedSelector);
  const snackMessage = useSelector(getSnackMessage);
  const liveTextStatus = useSelector(getLiveTextStatus);
  const [tabIndex, setTabIndex] = useState(LeftPanelTabIndexes.prep);
  const isSingleFile = useSelector(getIsSingleFile);
  const eitherFileMissingLiveText = liveTextStatus.source || liveTextStatus.target;
  const bothFilesMissingLiveText = liveTextStatus.source && liveTextStatus.target && !isSingleFile;
  const { refetchStatuses } = useInspectionCustomStatuses(inspectionId);
  const { isShared } = useCollaborators(inspectionId);
  const { isImported } = useSelector(getInspectionSettings);
  const prepLocked = isPrepLocked({
    routeInspectionId: id,
    loadedInspectionId: inspectionId,
    isShared,
    isImported,
  });

  // TODO: should this be inside an useEffect?
  // we keep the urls because a lot of other stuff is based on the url we are currently viewing (we get it from window.location)
  const urls = {
    [LeftPanelTabIndexes.prep]: `/inspection/${inspectionId}`,
    [LeftPanelTabIndexes.result]: `/inspection/${inspectionId}/result`,
  };

  const getTabUrl = (tabIndex: LeftPanelTabIndexes) => {
    let url = urls[tabIndex];
    if (location.search) {
      url = `${url}${location.search}`;
    }
    return url;
  };

  // Update the selected tab on mount
  useEffect(() => {
    const getValueFromUrl = () => {
      let valueFromUrl = LeftPanelTabIndexes.prep;
      if (isResultUrl(location)) {
        valueFromUrl = LeftPanelTabIndexes.result;
      }
      return valueFromUrl;
    };

    const urlTabIndex = getValueFromUrl();
    setTabIndex(urlTabIndex);

    PDFManagerFactory.getPDFDocManager()?.resetPDFTronTool();
    dispatch(inspection.actions.setSelectedTool({ tool: PDFTRON_DEFAULT_TOOL }));
    dispatch(inspection.actions.unfocusDifference());
    dispatch(app.actions.setStartingInspection(false));

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, inspectionId]);

  useEffect(() => {
    if (id && id !== inspectionId) {
      Sentry.setTag('inspectionId', id);
      expireAllToast();
      dispatch(fetchInspection(id, 'session'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    // redirect to results when differences are loaded
    if (!isReportsUrl() && inspectionCompleted && inspectionId && differenceLoaded) {
      navigate(getTabUrl(LeftPanelTabIndexes.result));
      refetchStatuses();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inspectionCompleted, inspectionId, differenceLoaded]);

  // 0 -> prep, 1 -> results, 2 -> report
  const handleChange = (_event: React.ChangeEvent<{}>, newTabIndex: 0 | 1) => {
    if (snackMessage && snackMessage.open && snackMessage.type === 'info') {
      dispatch(app.actions.clearSnackMessageType('info'));
    }
    expireAllToast();

    if (newTabIndex === 0 || newTabIndex === 1) {
      dispatch(app.actions.setHideDisplayPanels({ source: false, target: false }));
    }

    if (newTabIndex !== 1) {
      // stop any flashing
      const docManager = PDFManagerFactory.getPDFDocManager();
      if (!docManager) return;
      FlashAnimationManager.stopAllAnimations(docManager.source, docManager.target);
      dispatch(app.actions.setFocusedMatchIndex(-1));
    }

    // segment event for corresponding tab page
    if (tabIndex !== newTabIndex) {
      tracker.track({ name: segmentTabNames[newTabIndex] });
    }

    setTabIndex(newTabIndex);
  };

  useEffect(() => {
    if (eitherFileMissingLiveText) {
      dispatch(
        inspection.actions.setInspectionSettings({
          text: false,
          textMatchingMode: undefined,
        }),
      );
    }
  }, [
    liveTextStatus.source,
    liveTextStatus.target,
    isSingleFile,
    bothFilesMissingLiveText,
    dispatch,
    eitherFileMissingLiveText,
  ]);

  const sharedInspectionTooltip =
    'This inspection is shared. Re-running is disabled to keep results consistent. Stop sharing to unlock Prep and make changes';

  const renderPrepTab = () => {
    // Default to disabled until we confirm the inspection is not shared.
    // VerifyAPI imports stay shared and still get Prep.
    const isPrepTabEnabled = documentsLoaded && !prepLocked;

    const prepTab = (
      <Tab
        data-testid="left_panel_prep_tab"
        className={styles.tab}
        component={prepLocked ? 'div' : Link}
        to={!prepLocked ? getTabUrl(LeftPanelTabIndexes.prep) : undefined}
        label={<TabLabel label="Prep" fontSize={'12px'} showLock={prepLocked} disabled={prepLocked} />}
        disabled={!isPrepTabEnabled}
      />
    );

    if (prepLocked) {
      return (
        <GVTooltip title={sharedInspectionTooltip} side="bottom" className={styles.tooltip}>
          <div>{prepTab}</div>
        </GVTooltip>
      );
    }

    return prepTab;
  };

  return (
    <div className={styles.root}>
      <AppBar position="static">
        <Tabs
          indicatorColor="secondary"
          value={tabIndex}
          onChange={handleChange}
          scrollButtons={false}
          className={styles.tabs}
        >
          {renderPrepTab()}
          <Tab
            data-testid="left_panel_results_tab"
            className={styles.tab}
            component={Link}
            to={getTabUrl(LeftPanelTabIndexes.result)}
            label={
              <TabLabel
                label="Results"
                warning={inspectionIsOutdated || outdatedGraphicsTool || isProfileStale}
                tooltipTitle={
                  inspectionIsOutdated
                    ? 'Results do not reflect changes. Please press inspect in PREP stage to update.'
                    : isProfileStale
                      ? 'The selected custom profile has been modified since the last inspection. Please re-inspect to update results.'
                      : 'Outdated zone tool. Please press inspect in PREP stage to update.'
                }
                fontSize={'12px'}
              />
            }
            disabled={!inspectionHasRun || !documentsLoaded}
          />
        </Tabs>
      </AppBar>
      <div className={styles.tabContent}>
        <TabPanel value={tabIndex} index={LeftPanelTabIndexes.prep}>
          {tabIndex === LeftPanelTabIndexes.prep ? <PrepTab /> : <></>}
        </TabPanel>
        <TabPanel value={tabIndex} index={LeftPanelTabIndexes.result}>
          <ResultTab />
        </TabPanel>
      </div>
    </div>
  );
};

export default InspectionLeftPanel;
